
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart, CartItem } from '../contexts/CartContext';
import { PageHeader, Card, Table, Thead, Tbody, Tr, Th, Td, Button } from '../components/UI';
import { ShoppingCart, Trash2, Plus, Minus, ArrowRight, Shield, FileText, Check } from '../components/Icons';

const EnterpriseRequestCart: React.FC = () => {
  const { items, removeItem, updateQuantity, clearCart, totalItems, totalPrice } = useCart();
  const navigate = useNavigate();

  const handleCreateRequests = () => {
    alert('Purchase requests generated and sent to suppliers via EDI.');
    clearCart();
    navigate('/orders');
  };

  if (items.length === 0) {
    return (
      <div className="p-6 space-y-6 max-w-[1400px] mx-auto animate-in fade-in duration-500">
        <PageHeader title="Consignment Request" breadcrumbs={[{ label: 'Supply Chain' }, { label: 'Request Cart' }]} />
        <Card className="flex flex-col items-center justify-center py-32 border-dashed border-2 bg-transparent rounded-lg">
           <ShoppingCart size={48} className="text-gray-300 mb-4" />
           <h3 className="text-lg font-bold text-brand-dark dark:text-white uppercase tracking-widest">Cart Empty</h3>
           <p className="text-xs text-gray-400 mt-2 font-medium">No items pending consignment request.</p>
           <Button onClick={() => navigate('/replenishment')} className="mt-6">GO TO CONSIGNMENT</Button>
        </Card>
      </div>
    );
  }

  // Group items by Supplier to simulate separate POs
  const groupedItems = items.reduce((acc, item) => {
    const supplier = item.supplier || 'Unknown Supplier';
    if (!acc[supplier]) acc[supplier] = [];
    acc[supplier].push(item);
    return acc;
  }, {} as Record<string, CartItem[]>);

  return (
    <div className="p-6 lg:p-10 space-y-8 max-w-[1920px] mx-auto animate-in fade-in duration-500">
      <div className="flex items-start justify-between">
         <PageHeader 
            title="Finalize Requests" 
            breadcrumbs={[{ label: 'Supply Chain' }, { label: 'Request Cart' }]}
         />
         <button onClick={clearCart} className="flex items-center gap-2 text-red-500 hover:text-red-700 text-[10px] font-bold uppercase tracking-widest transition-colors mt-2">
            <Trash2 size={16} /> Clear All
         </button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-10">
        <div className="xl:col-span-8 space-y-8">
          {Object.entries(groupedItems).map(([supplier, supplierItems]: [string, CartItem[]]) => (
             <Card key={supplier} className="overflow-hidden border-t-4 border-t-brand-medium">
               <div className="px-6 py-4 bg-gray-50 dark:bg-[#1A1A1A] border-b border-gray-200 dark:border-[#333] flex justify-between items-center">
                  <div className="flex items-center gap-3">
                     <div className="w-8 h-8 rounded bg-white dark:bg-[#262626] border border-gray-200 dark:border-[#333] flex items-center justify-center text-gray-500">
                        <FileText size={16} />
                     </div>
                     <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Supplier</p>
                        <h4 className="text-sm font-bold text-brand-dark dark:text-white">{supplier}</h4>
                     </div>
                  </div>
                  <div className="text-right">
                     <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">Draft PO Value</p>
                     <p className="text-sm font-bold text-brand-dark dark:text-white">CHF {supplierItems.reduce((sum, i) => sum + (i.price * i.quantity), 0).toFixed(2)}</p>
                  </div>
               </div>
               <Table>
                 <Thead>
                   <Tr isHeader>
                     <Th>Product</Th>
                     <Th className="text-right">Qty</Th>
                     <Th className="text-right">Est. Cost</Th>
                     <Th></Th>
                   </Tr>
                 </Thead>
                 <Tbody>
                   {supplierItems.map((item) => (
                     <Tr key={item.id}>
                       <Td>
                         <div className="font-bold text-brand-dark dark:text-white">{item.itemName}</div>
                         <div className="text-[10px] text-gray-400 font-mono uppercase">{item.itemNumber}</div>
                       </Td>
                       <Td className="text-right">
                         <div className="flex items-center justify-end gap-3">
                           <button onClick={() => updateQuantity(item.id, item.quantity - 1)} className="p-1 hover:bg-gray-100 dark:hover:bg-[#333] rounded"><Minus size={12} /></button>
                           <span className="text-sm font-black w-8 text-center">{item.quantity}</span>
                           <button onClick={() => updateQuantity(item.id, item.quantity + 1)} className="p-1 hover:bg-gray-100 dark:hover:bg-[#333] rounded"><Plus size={12} /></button>
                         </div>
                       </Td>
                       <Td className="text-right font-medium">CHF {(item.price * item.quantity).toFixed(2)}</Td>
                       <Td className="text-right">
                         <button onClick={() => removeItem(item.id)} className="text-gray-300 hover:text-red-500"><Trash2 size={16} /></button>
                       </Td>
                     </Tr>
                   ))}
                 </Tbody>
               </Table>
             </Card>
          ))}
        </div>

        <div className="xl:col-span-4 space-y-6">
          <Card className="p-10 space-y-8 bg-brand-dark text-white border-none shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -mr-24 -mt-24 blur-3xl"></div>
            <h3 className="text-lg font-bold uppercase tracking-[0.2em] relative z-10">Summary</h3>
            <div className="space-y-4 relative z-10">
              <div className="flex justify-between text-white/50 text-[10px] font-black uppercase tracking-widest">
                <span>Total Items</span>
                <span className="text-white">{totalItems}</span>
              </div>
              <div className="flex justify-between text-white/50 text-[10px] font-black uppercase tracking-widest">
                <span>Suppliers</span>
                <span className="text-white">{Object.keys(groupedItems).length}</span>
              </div>
              <div className="pt-8 border-t border-white/10 flex justify-between items-center">
                <span className="text-xs font-black uppercase tracking-widest text-white/80">Est. Total</span>
                <span className="text-4xl font-black text-brand-bright">CHF {totalPrice.toLocaleString('de-CH')}</span>
              </div>
            </div>
            <Button onClick={handleCreateRequests} className="w-full h-16 !bg-brand-bright !text-brand-dark font-black tracking-[0.2em] shadow-xl hover:brightness-110 active:scale-95 transition-all">
              GENERATE REQUESTS <ArrowRight size={16} className="ml-2" />
            </Button>
          </Card>

          <Card className="p-6 bg-white dark:bg-[#1A1A1A] border-l-4 border-l-brand-medium flex items-start gap-4">
             <Shield size={20} className="text-brand-medium shrink-0" />
             <div className="space-y-1">
                <h4 className="text-[10px] font-black uppercase tracking-widest text-brand-dark dark:text-white">Workflow Logic</h4>
                <p className="text-[11px] text-gray-500 leading-relaxed font-medium">
                  Requests will be routed via EDI to connected suppliers. Non-EDI suppliers will receive email notifications.
                </p>
             </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EnterpriseRequestCart;
